import React, { useState } from 'react';
import { Calculator, ExternalLink, Maximize2, Minimize2, RefreshCw, Info, Percent, CalendarDays } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Calculadora = () => {
  const [activeTab, setActiveTab] = useState('plazos');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoadingPlazos, setIsLoadingPlazos] = useState(true);
  const [isLoadingIntereses, setIsLoadingIntereses] = useState(true);

  const urlPlazos = 'https://www.calculo-plazosprocesales.com/';
  const urlIntereses = 'https://www.calculo-intereses.com/';

  const handleIframePlazosLoad = () => {
    setIsLoadingPlazos(false);
  };

  const handleIframeInteresesLoad = () => {
    setIsLoadingIntereses(false);
  };

  const refreshIframe = () => {
    if (activeTab === 'plazos') {
      setIsLoadingPlazos(true);
      const iframe = document.getElementById('calculadora-plazos-iframe');
      if (iframe) {
        iframe.src = iframe.src;
      }
    } else {
      setIsLoadingIntereses(true);
      const iframe = document.getElementById('calculadora-intereses-iframe');
      if (iframe) {
        iframe.src = iframe.src;
      }
    }
  };

  const openInNewTab = () => {
    const url = activeTab === 'plazos' ? urlPlazos : urlIntereses;
    window.open(url, '_blank');
  };

  return (
    <div className={`p-6 space-y-4 ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-blue-600" />
            Calculadoras
          </h1>
          <p className="text-gray-600 mt-1">
            Herramientas de cálculo para plazos procesales e intereses legales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={refreshIframe}
            title="Recargar"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={openInNewTab}
            title="Abrir en nueva pestaña"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
          <TabsTrigger value="plazos" className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Calculadora de Plazos
          </TabsTrigger>
          <TabsTrigger value="intereses" className="flex items-center gap-2">
            <Percent className="w-4 h-4" />
            Calculadora de Intereses
          </TabsTrigger>
        </TabsList>

        {/* TAB: Calculadora de Plazos */}
        <TabsContent value="plazos" className="space-y-4">
          {/* Info Banner */}
          {!isFullscreen && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-900 font-medium">Calculadora oficial de plazos procesales</p>
                    <p className="text-blue-700 mt-1">
                      Esta herramienta tiene en cuenta los días inhábiles de cada partido judicial, 
                      festivos nacionales, autonómicos y locales. Se actualiza anualmente para garantizar 
                      cálculos precisos.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-blue-700 border-blue-300 flex-shrink-0">
                    Herramienta externa
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Iframe Container */}
          <Card className={`overflow-hidden ${isFullscreen ? 'h-[calc(100vh-180px)]' : ''}`}>
            <CardContent className="p-0 relative">
              {/* Loading indicator */}
              {isLoadingPlazos && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Cargando calculadora de plazos...</p>
                  </div>
                </div>
              )}
              
              {/* Iframe */}
              <iframe
                id="calculadora-plazos-iframe"
                src={urlPlazos}
                title="Calculadora de Plazos Procesales"
                className={`w-full border-0 ${isFullscreen ? 'h-[calc(100vh-190px)]' : 'h-[650px]'}`}
                onLoad={handleIframePlazosLoad}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                loading="lazy"
              />
            </CardContent>
          </Card>

          {/* Footer info */}
          {!isFullscreen && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>
                Fuente: <a href={urlPlazos} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  www.calculo-plazosprocesales.com
                </a>
              </p>
              <p>
                Los cálculos tienen en cuenta la normativa procesal vigente y el calendario judicial oficial
              </p>
            </div>
          )}
        </TabsContent>

        {/* TAB: Calculadora de Intereses */}
        <TabsContent value="intereses" className="space-y-4">
          {/* Info Banner */}
          {!isFullscreen && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-green-900 font-medium">Calculadora de intereses legales y de demora</p>
                    <p className="text-green-700 mt-1">
                      Calcula intereses legales del dinero, intereses de demora, intereses procesales y 
                      anatocismo. Incluye tipos de interés actualizados según el BOE.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-green-700 border-green-300 flex-shrink-0">
                    Herramienta externa
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Iframe Container */}
          <Card className={`overflow-hidden ${isFullscreen ? 'h-[calc(100vh-180px)]' : ''}`}>
            <CardContent className="p-0 relative">
              {/* Loading indicator */}
              {isLoadingIntereses && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Cargando calculadora de intereses...</p>
                  </div>
                </div>
              )}
              
              {/* Iframe */}
              <iframe
                id="calculadora-intereses-iframe"
                src={urlIntereses}
                title="Calculadora de Intereses"
                className={`w-full border-0 ${isFullscreen ? 'h-[calc(100vh-190px)]' : 'h-[650px]'}`}
                onLoad={handleIframeInteresesLoad}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                loading="lazy"
              />
            </CardContent>
          </Card>

          {/* Footer info */}
          {!isFullscreen && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>
                Fuente: <a href={urlIntereses} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                  www.calculo-intereses.com
                </a>
              </p>
              <p>
                Tipos de interés actualizados según publicación oficial en el BOE
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Calculadora;
